import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Box, Divider, HStack, Text, VStack } from '@chakra-ui/react';
import { FileSignature, ExternalLink } from 'lucide-react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import EnrolmentDocumentButtonAsync from '@duke-hq/ui/components/PdfGeneratorButton/EnrolmentDocumentButtonAsync';
import { DOCUMENT_TYPE_LABELS } from '@duke-hq/svc-api/lib/enrolment/formatPdfDocumentData';
import {
  completionDocumentTypes,
  type CompletionsProgressionsCategory,
} from '@duke-hq/svc-api/lib/enrolment/listAvailableEnrolmentDocuments';
import { djs } from '@duke-hq/djs';
import ContentContainer from './shared/ContentContainer';
import EnrolmentCard from './shared/EnrolmentCard';
import SidePanelContentHeader from './shared/ContentHeader';
import NoDataMessage from './shared/NoDataMessage';

type Props = {
  category?: CompletionsProgressionsCategory;
};

type AvailableDocument = {
  documentType: keyof typeof DOCUMENT_TYPE_LABELS;
  documentDisplayName: string;
  documentSemester?: 1 | 2;
};

type EnrolmentCardFooterProps = {
  enrolmentId: number;
  availableDocuments: AvailableDocument[];
};

function enrolmentDocumentProcedure() {
  return (
    {
      documentType,
      enrolmentId,
      semester,
    }: {
      documentType: Parameters<typeof EnrolmentDocumentButtonAsync>[0]['documentType'];
      enrolmentId: number;
      tenantId?: number;
      semester?: number;
    },
    options: { enabled: boolean; retry: boolean },
  ): { refetch: () => Promise<{ data }> } => {
    const userUTCOffset = djs().utcOffset();

    const queryResult = trpcTenant.enrolments.getPdfDocumentData.useQuery(
      {
        documentType,
        enrolmentId,
        semester,
        userUTCOffset,
      },
      options,
    );

    return {
      refetch: async () => {
        const result = await queryResult.refetch();
        return { data: result.data };
      },
    };
  };
}

function EnrolmentCardFooter({ enrolmentId, availableDocuments }: EnrolmentCardFooterProps) {
  const refetchEnrolmentDocument = enrolmentDocumentProcedure();

  return (
    <VStack spacing={2} align="stretch" w="full">
      <Divider my={1} />
      {availableDocuments.map((doc, index) => {
        const documentColour = completionDocumentTypes.includes(doc.documentType)
          ? 'green'
          : 'blue';

        return (
          <Box key={`${enrolmentId}-${doc.documentType}-${doc.documentSemester ?? index}`} w="full">
            <HStack justifyContent="space-between" w="full">
              <Text
                fontSize="xs"
                color={`${documentColour}.500`}
                noOfLines={1}
                title={doc.documentDisplayName}
                flexShrink={1}
                mr={2}
              >
                {doc.documentDisplayName}
              </Text>
              <EnrolmentDocumentButtonAsync
                procedureQuery={refetchEnrolmentDocument}
                documentType={doc.documentType}
                enrolmentId={enrolmentId}
                semester={doc.documentSemester}
                colorScheme={`${documentColour}`}
                size="xs"
                fontSize="xs"
                height={6}
                gap="4px"
              >
                <Trans>View</Trans>
                <ExternalLink size="12" />
              </EnrolmentDocumentButtonAsync>
            </HStack>
          </Box>
        );
      })}
    </VStack>
  );
}

function getHeaderCopy(category: CompletionsProgressionsCategory) {
  switch (category) {
    case 'progressions':
      return {
        title: t`Progress reports and VOE`,
        tooltipText: t`Newly available progress reports and Verification of Education documents`,
      };
    case 'all':
      return {
        title: t`Completion and progression documents`,
        tooltipText: t`Newly available Statements of Attainment, certificates, progress reports and Verification of Education documents`,
      };
    case 'completions':
      return {
        title: t`Recently completed`,
        tooltipText: t`Newly available Statements of Attainment and certificates`,
      };
  }
}

function getContainerTitle(category: CompletionsProgressionsCategory) {
  switch (category) {
    case 'progressions':
      return t`Progressions - Last 14 days`;
    case 'all':
      return t`Completions and progression - Last 14 days`;
    case 'completions':
      return t`Completions - Last 14 days`;
  }
}

function CompletionsProgressionsWithData({ category = 'completions' }: Props) {
  const { filters } = useFiltersContext();
  const userUTCOffset = djs().utcOffset();
  const { data } = trpcTenant.dashboard.getCompletionsProgressions.useQuery(
    { filters, category, userUTCOffset },
    { suspense: true },
  );

  if (!data || data.length === 0) {
    return <NoDataMessage />;
  }

  const { title, tooltipText } = getHeaderCopy(category);

  return (
    <>
      <SidePanelContentHeader title={title} tooltipText={tooltipText} />
      <VStack>
        {data.map((enrolment) => (
          <EnrolmentCard
            key={enrolment.id}
            studentName={enrolment.studentName}
            courseTitle={enrolment.courseTitle}
            courseIdentifier={enrolment.courseIdentifier}
            headerText={enrolment.contractType}
            HeaderIcon={FileSignature}
            Footer={
              <EnrolmentCardFooter
                enrolmentId={enrolment.id}
                availableDocuments={enrolment.availableDocuments}
              />
            }
          />
        ))}
      </VStack>
    </>
  );
}

export default function CompletionsProgressions({ category = 'completions' }: Props) {
  return (
    <ContentContainer title={getContainerTitle(category)}>
      <CompletionsProgressionsWithData category={category} />
    </ContentContainer>
  );
}
